import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { type DateWindow, dateWindowSchema } from './dateWindow'

import styles from './PrereadFeedback.module.scss'

import type { EvidenceScope, PrereadFilters } from '@/api/prereadFeedback'
import Button from '@/components/Button'
import Input from '@/components/Input'
import Select from '@/components/Select'

type Props = {
  since: string
  until: string
  source: PrereadFilters['source']
  rating: PrereadFilters['rating']
  model: PrereadFilters['model']
  scope: EvidenceScope
  isUsage: boolean
  isRefreshDisabled: boolean
  onChange: (key: 'source' | 'rating' | 'model' | 'scope', value: string) => void
  onApplyDates: (dates: DateWindow) => void
  onRefresh: () => void
}

const FeedbackFilters = ({
  since,
  until,
  source,
  rating,
  model,
  scope,
  isUsage,
  isRefreshDisabled,
  onChange,
  onApplyDates,
  onRefresh,
}: Props) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<DateWindow>({
    resolver: zodResolver(dateWindowSchema),
    values: { since, until },
  })
  const dateError = errors.since?.message ?? errors.until?.message
  return (
    <>
      <form
        className={styles.filters}
        onSubmit={handleSubmit(onApplyDates)}
        aria-label="Preread feedback filters"
        noValidate
      >
        <label>
          From (UTC)
          <Controller
            name="since"
            control={control}
            render={({ field }) => (
              <Input type="date" value={field.value} onInput={field.onChange} ariaLabel="From date (UTC)" />
            )}
          />
        </label>
        <label>
          Before (UTC)
          <Controller
            name="until"
            control={control}
            render={({ field }) => (
              <Input type="date" value={field.value} onInput={field.onChange} ariaLabel="Before date (UTC)" />
            )}
          />
        </label>
        <Button title="Apply dates" type="light" htmlType="submit" onClick={() => undefined} />
        <label>
          Model
          <Select
            value={model ?? ''}
            onChange={(value) => onChange('model', value)}
            ariaLabel="Brief model"
            options={[
              { value: '', label: 'All models' },
              { value: 'luna', label: 'Luna' },
              { value: 'claude', label: 'Claude' },
            ]}
          />
        </label>
        <label>
          Source
          <Select
            value={source ?? ''}
            onChange={(value) => onChange('source', value)}
            ariaLabel="Feedback source"
            options={[
              { value: '', label: 'Internal and external' },
              { value: 'internal', label: 'Internal' },
              { value: 'external', label: 'External' },
            ]}
          />
        </label>
        <label>
          Rating
          <Select
            value={rating ?? ''}
            onChange={(value) => onChange('rating', value)}
            ariaLabel="Feedback rating"
            options={[
              { value: '', label: 'All ratings' },
              { value: 'up', label: 'Thumbs up' },
              { value: 'down', label: 'Thumbs down' },
            ]}
          />
        </label>
        {isUsage && (
          <label>
            Population
            <Select
              value={scope}
              onChange={(value) => onChange('scope', value)}
              ariaLabel="Evidence population"
              options={[
                { value: 'all', label: 'All generated prereads' },
                { value: 'reported', label: 'Reported briefs only' },
              ]}
            />
          </label>
        )}
        <Button title="Refresh" type="light" isDisabled={isRefreshDisabled} onClick={onRefresh} />
      </form>
      {dateError && (
        <p role="alert" className={styles.notice}>
          {dateError}
        </p>
      )}
    </>
  )
}

export default FeedbackFilters
